import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

const getPayload = (rawPayload) => {
    if (!rawPayload || typeof rawPayload !== 'object') {
        return {};
    }

    return rawPayload;
};

const buildDataUrl = (dataUrl, labels, page, perPage, classFilter, refresh = false) => {
    const url = new URL(dataUrl, window.location.origin);
    labels.forEach((label) => {
        url.searchParams.append('labels[]', label);
    });
    url.searchParams.set('page', String(page));
    url.searchParams.set('per_page', String(perPage));
    if (classFilter.trim() !== '') {
        url.searchParams.set('class_filter', classFilter.trim());
    }
    if (refresh) {
        url.searchParams.set('refresh', '1');
        url.searchParams.set('_ts', String(Date.now()));
    }

    return url.toString();
};

const getContrastColor = (hexColor) => {
    if (typeof hexColor !== 'string' || !hexColor.startsWith('#') || hexColor.length !== 7) {
        return '#ffffff';
    }

    const red = parseInt(hexColor.slice(1, 3), 16) || 0;
    const green = parseInt(hexColor.slice(3, 5), 16) || 0;
    const blue = parseInt(hexColor.slice(5, 7), 16) || 0;
    const luma = (0.299 * red) + (0.587 * green) + (0.114 * blue);

    return luma > 160 ? '#111111' : '#ffffff';
};

const getDirectorySubheader = (directory) => {
    const parts = [];

    if (directory.fluxName && !directory.fluxName.startsWith('LabelFlux for ')) {
        parts.push(directory.fluxName);
    }

    if (directory.modelName) {
        parts.push(`Model: ${directory.modelName}`);
    }

    return parts.length ? parts.join(' · ') : 'No linked flux';
};

const getDirectoryDisplayLabel = (directory) => {
    if (directory.fluxName && !directory.fluxName.startsWith('LabelFlux for ')) {
        return directory.fluxName;
    }

    return directory.name;
};

function CompareDirectoryCard({
    directory,
    classes,
    zoom,
    classFilter,
    onOpenLightbox,
    onCopyDebug,
    largeMode = false,
}) {
    const [selectedClassKey, setSelectedClassKey] = useState(null);
    const [hoveredClassKey, setHoveredClassKey] = useState(null);
    const imagePaneRef = useRef(null);
    const middlePanStateRef = useRef({
        active: false,
        startX: 0,
        startY: 0,
        scrollLeft: 0,
        scrollTop: 0,
    });

    const classMap = useMemo(() => {
        return new Map(classes.map((entry) => [entry.key, entry]));
    }, [classes]);

    const visibleClasses = useMemo(() => {
        const normalizedFilter = classFilter.trim().toLowerCase();

        return Object.entries(directory.counts || {})
            .map(([key, count]) => {
                const entry = classMap.get(key);
                if (!entry || count <= 0) {
                    return null;
                }

                return {
                    ...entry,
                    count,
                };
            })
            .filter(Boolean)
            .filter((entry) => {
                return normalizedFilter === '' || entry.name.toLowerCase().includes(normalizedFilter);
            })
            .sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true, sensitivity: 'base' }));
    }, [classFilter, classMap, directory.counts]);

    const highlightedClassKey = selectedClassKey || hoveredClassKey;

    const handleClassClick = useCallback((classKey) => {
        setSelectedClassKey((current) => (current === classKey ? null : classKey));
    }, []);

    const handleClearSelection = useCallback(() => {
        setSelectedClassKey(null);
    }, []);

    const handleCopyDebug = useCallback(async (label) => {
        const payload = {
            class: label.label,
            key: label.key,
            left_pct: Number(label.left.toFixed(3)),
            top_pct: Number(label.top.toFixed(3)),
            width_pct: Number(label.width.toFixed(3)),
            height_pct: Number(label.height.toFixed(3)),
        };
        const text = JSON.stringify(payload, null, 2);

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            }
        } catch (error) {
            console.warn('Failed to copy compare label debug payload', error);
        }

        if (typeof onCopyDebug === 'function') {
            onCopyDebug(`${label.label} debug copied.`);
        }
    }, [onCopyDebug]);

    const handleImagePaneMouseDown = useCallback((event) => {
        if (!largeMode || event.button !== 1 || !imagePaneRef.current) {
            return;
        }

        event.preventDefault();
        middlePanStateRef.current = {
            active: true,
            startX: event.clientX,
            startY: event.clientY,
            scrollLeft: imagePaneRef.current.scrollLeft,
            scrollTop: imagePaneRef.current.scrollTop,
        };
    }, [largeMode]);

    const handleImagePaneMouseMove = useCallback((event) => {
        if (!middlePanStateRef.current.active || !imagePaneRef.current) {
            return;
        }

        event.preventDefault();
        const deltaX = event.clientX - middlePanStateRef.current.startX;
        const deltaY = event.clientY - middlePanStateRef.current.startY;

        imagePaneRef.current.scrollLeft = middlePanStateRef.current.scrollLeft - deltaX;
        imagePaneRef.current.scrollTop = middlePanStateRef.current.scrollTop - deltaY;
    }, []);

    const stopImagePaneMiddlePan = useCallback(() => {
        middlePanStateRef.current.active = false;
    }, []);

    const imagePane = (
        <Box
            ref={imagePaneRef}
            onMouseDown={handleImagePaneMouseDown}
            onMouseMove={handleImagePaneMouseMove}
            onMouseUp={stopImagePaneMiddlePan}
            onMouseLeave={stopImagePaneMiddlePan}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: '#fafafa',
                overflow: 'auto',
                minHeight: largeMode ? 520 : undefined,
                maxHeight: largeMode ? 'calc(100vh - 280px)' : undefined,
                cursor: largeMode && !directory.missing ? 'grab' : 'default',
                userSelect: 'none',
            }}
        >
            {directory.missing ? (
                <Box
                    sx={{
                        minHeight: 260,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        px: 3,
                        py: 4,
                        color: 'text.secondary',
                        border: '2px dashed',
                        borderColor: 'divider',
                    }}
                >
                    <Stack spacing={1} alignItems="center">
                        <Typography variant="subtitle2">Missing in this directory</Typography>
                        <Typography variant="body2">
                            The current filename does not exist here, so the compare view keeps the slot as a placeholder.
                        </Typography>
                    </Stack>
                </Box>
            ) : (
                <Box sx={{ display: 'inline-block', width: `${Math.max(0.2, zoom) * 100}%`, position: 'relative' }}>
                    <img
                        src={directory.previewUrl}
                        alt={directory.filename || directory.name}
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                    />
                    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                        {directory.labels.map((label) => (
                            <Tooltip
                                key={`${directory.name}-${label.key}-${label.left}-${label.top}`}
                                title={(
                                    <Stack spacing={0.5}>
                                        <Typography variant="caption" sx={{ color: '#fff' }}>{label.label}</Typography>
                                        <Typography variant="caption" sx={{ color: '#fff' }}>
                                            x: {label.left.toFixed(2)}% · y: {label.top.toFixed(2)}%
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#fff' }}>
                                            w: {label.width.toFixed(2)}% · h: {label.height.toFixed(2)}%
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#fff' }}>
                                            Click to copy debug payload
                                        </Typography>
                                    </Stack>
                                )}
                            >
                                <Box
                                    onClick={() => handleCopyDebug(label)}
                                    sx={{
                                        position: 'absolute',
                                        left: `${label.left}%`,
                                        top: `${label.top}%`,
                                        width: `${label.width}%`,
                                        height: `${label.height}%`,
                                        border: '2px solid',
                                        borderColor: label.color,
                                        backgroundColor: `${label.color}${highlightedClassKey === label.key ? '2f' : '14'}`,
                                        boxSizing: 'border-box',
                                        pointerEvents: 'auto',
                                        cursor: 'copy',
                                        opacity: highlightedClassKey && highlightedClassKey !== label.key ? 0.18 : 1,
                                        transition: 'opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
                                        boxShadow: highlightedClassKey === label.key ? `0 0 0 2px ${label.color}, 0 0 18px ${label.color}` : 'none',
                                        transform: highlightedClassKey === label.key ? 'scale(1.01)' : 'none',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: -22,
                                            left: 0,
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: 1,
                                            backgroundColor: label.color,
                                            color: getContrastColor(label.color),
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {label.label}
                                    </Box>
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );

    const classesPane = (
        <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Detected classes</Typography>
                {selectedClassKey ? (
                    <Button size="small" variant="text" onClick={handleClearSelection}>
                        Deselect
                    </Button>
                ) : null}
            </Stack>
            <TableContainer sx={{ maxHeight: largeMode ? 'calc(100vh - 280px)' : 260, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Class</TableCell>
                            <TableCell align="right">Count</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleClasses.length ? visibleClasses.map((entry) => (
                            <TableRow
                                key={`${directory.name}-${entry.key}`}
                                hover
                                selected={selectedClassKey === entry.key}
                                onMouseEnter={() => setHoveredClassKey(entry.key)}
                                onMouseLeave={() => setHoveredClassKey(null)}
                                onClick={() => handleClassClick(entry.key)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={entry.name}
                                        sx={{
                                            backgroundColor: entry.color,
                                            color: getContrastColor(entry.color),
                                            fontWeight: 600,
                                            maxWidth: '100%',
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">{entry.count}</TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        {directory.missing ? 'No detections because this image is missing in the directory.' : 'No detected classes match the current filter.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );

    return (
        <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
            <CardHeader
                title={directory.name}
                subheader={getDirectorySubheader(directory)}
                titleTypographyProps={{
                    sx: {
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        lineHeight: 1.2,
                        minHeight: '2.4em',
                    },
                }}
                subheaderTypographyProps={{
                    sx: {
                        display: 'block',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        lineHeight: 1.25,
                        minHeight: '2.5em',
                    },
                }}
                action={
                    <Stack direction="row" spacing={1}>
                        {!largeMode && !directory.missing && directory.fullUrl ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onOpenLightbox(directory)}
                            >
                                Fullscreen
                            </Button>
                        ) : null}
                        {directory.fluxUrl ? (
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                component="a"
                                href={directory.fluxUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Open flux
                            </Button>
                        ) : null}
                        <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            component="a"
                            href={directory.editUrl || directory.viewUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <LaunchIcon fontSize="small" />
                        </Button>
                    </Stack>
                }
                sx={{
                    pb: 1,
                    alignItems: 'flex-start',
                    minHeight: 112,
                }}
            />

            <CardContent sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: 'block',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        minHeight: '2.8em',
                    }}
                >
                    {directory.filename || 'Missing image'}
                </Typography>
                {largeMode ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' },
                            gap: 2,
                            alignItems: 'start',
                        }}
                    >
                        {imagePane}
                        {classesPane}
                    </Box>
                ) : (
                    <>
                        {imagePane}
                        {classesPane}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

export default function LabelsComparePage({ payload }) {
    const initialPayload = useMemo(() => getPayload(payload), [payload]);
    const selectedLabels = useMemo(() => {
        return Array.isArray(initialPayload.selectedLabels) ? initialPayload.selectedLabels : [];
    }, [initialPayload.selectedLabels]);
    const links = initialPayload.links || {};
    const initialDirectories = Array.isArray(initialPayload.directories) ? initialPayload.directories : [];

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [classFilter, setClassFilter] = useState('');
    const [draftClassFilter, setDraftClassFilter] = useState('');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxRowIndex, setLightboxRowIndex] = useState(0);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [lightboxZoom, setLightboxZoom] = useState(1);
    const [debugMessage, setDebugMessage] = useState('');

    const loadPage = useCallback(async (nextPage, options = {}) => {
        const {
            refresh = false,
            nextRowsPerPage = rowsPerPage,
            nextClassFilter = classFilter,
        } = options;

        if (!links.dataUrl || selectedLabels.length < 2) {
            setError('The compare view needs between 2 and 6 directories.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(
                buildDataUrl(links.dataUrl, selectedLabels, nextPage, nextRowsPerPage, nextClassFilter, refresh),
                {
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin',
                }
            );

            const json = await response.json().catch(() => ({}));
            if (!response.ok || json.ok !== true) {
                throw new Error(json.message || 'Unable to load comparison data.');
            }

            const directoryMeta = new Map(initialDirectories.map((entry) => [entry.name, entry]));
            const rows = (json.rows || []).map((row) => {
                const directories = (row.directories || []).map((directory) => ({
                    ...(directoryMeta.get(directory.name) || {}),
                    ...directory,
                }));

                return {
                    ...row,
                    directories,
                };
            });

            setData({
                rows,
                classes: json.classes || [],
                performance: json.performance || {},
                meta: json.meta || { totalItems: 0 },
            });
            setPage(json.meta?.page || nextPage);
            setRowsPerPage(json.meta?.perPage || nextRowsPerPage);
        } catch (fetchError) {
            console.error('Failed to load labels comparison data', fetchError);
            setError(fetchError.message || 'Unable to load comparison data.');
        } finally {
            setLoading(false);
        }
    }, [classFilter, initialDirectories, links.dataUrl, rowsPerPage, selectedLabels]);

    useEffect(() => {
        loadPage(1, { nextRowsPerPage: rowsPerPage, nextClassFilter: classFilter });
    }, [loadPage]);

    const currentMeta = data?.meta || { page: 1, lastPage: 1, totalItems: 0, perPage: rowsPerPage, startItem: 0, endItem: 0, unfilteredTotalItems: 0 };
    const currentRows = data?.rows || [];
    const currentClasses = data?.classes || [];
    const performance = data?.performance || {};
    const filteredRows = currentRows;
    const pageCount = currentMeta.lastPage || 1;
    const canGoPrev = page > 1;
    const canGoNext = page < pageCount;
    const visibleRows = filteredRows;
    const visibleStartItem = currentMeta.startItem || 0;
    const visibleEndItem = currentMeta.endItem || 0;
    const activeLightboxRow = filteredRows[lightboxRowIndex] || null;
    const activeLightboxDirectory = activeLightboxRow?.directories?.[lightboxIndex] || null;

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    useEffect(() => {
        if (lightboxRowIndex >= filteredRows.length) {
            setLightboxRowIndex(0);
        }
    }, [filteredRows.length, lightboxRowIndex]);

    useEffect(() => {
        const activeDirectoriesCount = activeLightboxRow?.directories?.length || 0;
        if (lightboxIndex >= activeDirectoriesCount) {
            setLightboxIndex(0);
        }
    }, [activeLightboxRow, lightboxIndex]);

    const handleChangePage = useCallback((nextPage) => {
        if (nextPage < 1 || nextPage > pageCount) {
            return;
        }
        loadPage(nextPage, { nextRowsPerPage: rowsPerPage, nextClassFilter: classFilter });
    }, [classFilter, loadPage, pageCount, rowsPerPage]);

    const handleRowsPerPageChange = useCallback((nextRowsPerPage) => {
        loadPage(1, { nextRowsPerPage, nextClassFilter: classFilter });
        setLightboxRowIndex(0);
    }, [classFilter, loadPage]);

    const handleRefreshLatest = useCallback(() => {
        loadPage(page, { refresh: true, nextRowsPerPage: rowsPerPage, nextClassFilter: classFilter });
    }, [classFilter, loadPage, page, rowsPerPage]);

    const handleApplyClassFilter = useCallback(() => {
        const nextClassFilter = draftClassFilter.trim();
        setClassFilter(nextClassFilter);
        loadPage(1, { nextRowsPerPage: rowsPerPage, nextClassFilter });
        setPage(1);
        setLightboxRowIndex(0);
    }, [draftClassFilter, loadPage, rowsPerPage]);

    const handleClearClassFilter = useCallback(() => {
        setDraftClassFilter('');
        setClassFilter('');
        loadPage(1, { nextRowsPerPage: rowsPerPage, nextClassFilter: '' });
        setPage(1);
        setLightboxRowIndex(0);
    }, [loadPage, rowsPerPage]);

    const handleOpenLightbox = useCallback((rowIndex, directoryIndex) => {
        setLightboxRowIndex(rowIndex);
        setLightboxIndex(directoryIndex);
        setLightboxZoom(1.35);
        setLightboxOpen(true);
    }, []);

    const handleChangeLightboxIndex = useCallback((nextIndex) => {
        const activeDirectoriesCount = activeLightboxRow?.directories?.length || 0;
        if (nextIndex < 0 || nextIndex >= activeDirectoriesCount) {
            return;
        }

        setLightboxIndex(nextIndex);
    }, [activeLightboxRow]);

    const handleChangeLightboxRow = useCallback((nextRowIndex) => {
        if (nextRowIndex < 0 || nextRowIndex >= filteredRows.length) {
            return;
        }

        setLightboxRowIndex(nextRowIndex);
    }, [filteredRows.length]);

    const handleCopyDebug = useCallback((message) => {
        setDebugMessage(message);
        window.setTimeout(() => {
            setDebugMessage('');
        }, 1800);
    }, []);

    const handleLightboxZoomOut = useCallback(() => {
        setLightboxZoom((current) => Math.max(0.2, Number((current - 0.2).toFixed(2))));
    }, []);

    const handleLightboxZoomIn = useCallback(() => {
        setLightboxZoom((current) => Math.min(5, Number((current + 0.2).toFixed(2))));
    }, []);

    return (
        <Box>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 3 }}>
                <Stack spacing={1}>
                    <Typography variant="h5" fontWeight={700}>Visual model compare</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Navigate one filename at a time across all selected directories. Missing files stay visible as placeholders.
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selectedLabels.map((label) => (
                            <Chip key={label} label={label} size="small" color="secondary" sx={{ mb: 1 }} />
                        ))}
                    </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Button variant="outlined" color="inherit" href={links.indexUrl || '/labels'}>
                        Back to labels
                    </Button>
                </Stack>
            </Stack>

            {performance.warning ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {performance.warning}
                </Alert>
            ) : null}

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between">
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {filteredRows.length
                                    ? `Images ${visibleStartItem}-${visibleEndItem} of ${filteredRows.length}`
                                    : 'No image available'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Page {page} of {pageCount} · {filteredRows.length} visible filenames{classFilter ? ` from ${currentMeta.unfilteredTotalItems || 0} total` : ''}
                            </Typography>
                            {classFilter ? (
                                <Typography variant="body2" color="text.secondary">
                                    Showing {currentMeta.totalItems || 0} matching rows for class filter "{classFilter}"
                                </Typography>
                            ) : null}
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                            <TextField
                                size="small"
                                label="Filter classes"
                                value={draftClassFilter}
                                onChange={(event) => setDraftClassFilter(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        handleApplyClassFilter();
                                    }
                                }}
                            />
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" color="text.secondary">Rows</Typography>
                                <Button
                                    variant={rowsPerPage === 5 ? 'contained' : 'outlined'}
                                    color="secondary"
                                    onClick={() => handleRowsPerPageChange(5)}
                                    disabled={loading}
                                >
                                    5
                                </Button>
                                <Button
                                    variant={rowsPerPage === 10 ? 'contained' : 'outlined'}
                                    color="secondary"
                                    onClick={() => handleRowsPerPageChange(10)}
                                    disabled={loading}
                                >
                                    10
                                </Button>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleApplyClassFilter}
                                >
                                    Apply filter
                                </Button>
                                <Button
                                    variant="text"
                                    color="inherit"
                                    onClick={handleClearClassFilter}
                                    disabled={draftClassFilter === '' && classFilter === ''}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={handleRefreshLatest}
                                    disabled={loading}
                                >
                                    Get latest
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => handleChangePage(1)}
                                    disabled={!canGoPrev || loading}
                                >
                                    First page
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<NavigateBeforeIcon />}
                                    onClick={() => handleChangePage(page - 1)}
                                    disabled={!canGoPrev || loading}
                                >
                                    Prev page
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    endIcon={<NavigateNextIcon />}
                                    onClick={() => handleChangePage(page + 1)}
                                    disabled={!canGoNext || loading}
                                >
                                    Next page
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => handleChangePage(pageCount)}
                                    disabled={!canGoNext || loading}
                                >
                                    Last page
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {loading ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={2}>
                    {visibleRows.map((row, rowIndex) => {
                        const gridColumns = Math.min(Math.max(row.directories.length, 1), 3);

                        return (
                            <Card key={row.key} variant="outlined">
                                <CardContent>
                                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                                        <Stack spacing={0.5}>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {row.filename}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Image {row.itemNumber} of {currentMeta.totalItems}
                                            </Typography>
                                        </Stack>

                                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                            <Chip
                                                size="small"
                                                color={row.isShared ? 'success' : 'warning'}
                                                label={row.isShared
                                                    ? 'Shared by all selected directories'
                                                    : `Present in ${row.presentCount}/${selectedLabels.length} directories`}
                                            />
                                            {row.missingCount > 0 ? (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    label={`Missing in ${row.missingCount}`}
                                                />
                                            ) : null}
                                        </Stack>
                                    </Stack>

                                    <Box sx={{ overflowX: 'auto', pb: 1 }}>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                    xs: '1fr',
                                                    md: `repeat(${Math.min(gridColumns, 2)}, minmax(340px, 1fr))`,
                                                    xl: `repeat(${gridColumns}, minmax(340px, 1fr))`,
                                                },
                                                gap: 2,
                                                minWidth: { xl: `${gridColumns * 360}px` },
                                            }}
                                        >
                                            {row.directories.map((directory, directoryIndex) => (
                                                <CompareDirectoryCard
                                                    key={`${row.key}-${directory.name}`}
                                                    directory={directory}
                                                    classes={currentClasses}
                                                    zoom={1}
                                                    classFilter={classFilter}
                                                    onOpenLightbox={() => handleOpenLightbox(rowIndex, directoryIndex)}
                                                    onCopyDebug={handleCopyDebug}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                    {!filteredRows.length ? (
                        <Alert severity="info">
                            No rows match the current class filter.
                        </Alert>
                    ) : null}
                </Stack>
            )}

            {debugMessage ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {debugMessage}
                </Alert>
            ) : null}

            <TablePagination
                component="div"
                count={currentMeta.totalItems || 0}
                page={Math.max(page - 1, 0)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10]}
                showFirstButton
                showLastButton
                onPageChange={(_, nextPage) => handleChangePage(nextPage + 1)}
                onRowsPerPageChange={(event) => handleRowsPerPageChange(Number(event.target.value) || 5)}
            />

            <Dialog
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                fullScreen
                sx={{ zIndex: 16000 }}
                BackdropProps={{ sx: { zIndex: 15999 } }}
                PaperProps={{ sx: { overflow: 'hidden', zIndex: 16000 } }}
            >
                <Card variant="outlined" sx={{ borderRadius: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>
                    <CardHeader
                        title={activeLightboxRow?.filename || 'Image preview'}
                        subheader="Comparison fullscreen"
                        action={
                            <IconButton onClick={() => setLightboxOpen(false)} sx={{ color: '#111' }}>
                                <CloseIcon />
                            </IconButton>
                        }
                    />
                    <Divider />
                    <DialogContent sx={{ backgroundColor: '#111', p: 2, overflow: 'auto', flex: 1 }}>
                        <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                                <Typography variant="body2" color="#fff">
                                    {activeLightboxRow?.filename || 'No image available'}
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        startIcon={<ZoomOutIcon />}
                                        onClick={handleLightboxZoomOut}
                                        disabled={lightboxZoom <= 0.2}
                                        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)' }}
                                    >
                                        Zoom out
                                    </Button>
                                    <Chip label={`Zoom ${Math.round(lightboxZoom * 100)}%`} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)' }} variant="outlined" />
                                    <Button
                                        variant="outlined"
                                        startIcon={<ZoomInIcon />}
                                        onClick={handleLightboxZoomIn}
                                        disabled={lightboxZoom >= 5}
                                        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)' }}
                                    >
                                        Zoom in
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<NavigateBeforeIcon />}
                                        onClick={() => handleChangeLightboxRow(lightboxRowIndex - 1)}
                                        disabled={lightboxRowIndex <= 0 || loading}
                                    >
                                        Prev image
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        endIcon={<NavigateNextIcon />}
                                        onClick={() => handleChangeLightboxRow(lightboxRowIndex + 1)}
                                        disabled={lightboxRowIndex >= filteredRows.length - 1 || loading}
                                    >
                                        Next image
                                    </Button>
                                </Stack>
                            </Stack>

                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ overflowX: 'auto', pb: 0.5 }}>
                                {(activeLightboxRow?.directories || []).map((directory, index) => (
                                    <Button
                                        key={`lightbox-switch-${directory.name}`}
                                        variant={index === lightboxIndex ? 'contained' : 'outlined'}
                                        color={index === lightboxIndex ? 'secondary' : 'inherit'}
                                        onClick={() => handleChangeLightboxIndex(index)}
                                        sx={index === lightboxIndex ? {} : { color: '#fff', borderColor: 'rgba(255,255,255,0.35)' }}
                                    >
                                        {getDirectoryDisplayLabel(directory)}
                                    </Button>
                                ))}
                            </Stack>

                            {activeLightboxDirectory ? (
                                <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2 }}>
                                    <CompareDirectoryCard
                                        directory={{
                                            ...activeLightboxDirectory,
                                            previewUrl: activeLightboxDirectory.fullUrl || activeLightboxDirectory.previewUrl,
                                        }}
                                        classes={currentClasses}
                                        zoom={lightboxZoom}
                                        classFilter={classFilter}
                                        onOpenLightbox={() => {}}
                                        onCopyDebug={handleCopyDebug}
                                        largeMode
                                    />
                                </Box>
                            ) : null}
                        </Stack>
                    </DialogContent>
                </Card>
            </Dialog>
        </Box>
    );
}